import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Compass, PenTool, CheckSquare, ArrowRight, Check, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { cldPresets } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = { Compass, PenTool, CheckSquare, Wrench };

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/services`)
      .then((res) => setServices(res.data || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  const renderIcon = (svc) => {
    const Icon = iconMap[svc.icon] || Compass;
    if (svc.icon && !iconMap[svc.icon]) {
      // If admin entered an emoji or short text, show it directly
      return <span className="text-3xl">{svc.icon}</span>;
    }
    return <Icon className="h-8 w-8" style={{ color: '#00CED1' }} />;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Our <span style={{ color: '#00CED1' }}>Services</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            From initial concept to final execution, we provide comprehensive architectural services
            that transform your vision into stunning reality.
          </p>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto space-y-20">
          {loading && <p className="text-center text-gray-500">Loading services…</p>}
          {!loading && services.length === 0 && (
            <div className="text-center text-gray-500">
              <p>No services published yet.</p>
              <p className="text-sm">Admin can add services from the Admin Portal → Services.</p>
            </div>
          )}
          {services.map((service, index) => {
            const isEven = index % 2 === 0;
            const imgSrc = service.image
              ? (service.image.startsWith('http') ? service.image : `${BACKEND_URL}${service.image}`)
              : null;

            return (
              <div
                key={service.id}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
                data-testid={`service-row-${service.id}`}
              >
                {/* Image (if any) */}
                {imgSrc && (
                  <div className="w-full lg:w-1/2">
                    <div className="relative group overflow-hidden rounded-2xl shadow-2xl">
                      <img
                        src={cldPresets.galleryCard(imgSrc)}
                        alt={service.title}
                        loading="lazy"
                        className="w-full h-[400px] object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ background: 'linear-gradient(to top, rgba(0, 206, 209, 0.8), transparent)' }}
                      />
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className={`w-full ${imgSrc ? 'lg:w-1/2' : ''}`}>
                  <Card className="border-0 shadow-none">
                    <CardContent className="p-0">
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                        style={{ backgroundColor: '#00CED115' }}
                      >
                        {renderIcon(service)}
                      </div>

                      <h2 className="text-3xl md:text-4xl font-bold mb-4">{service.title}</h2>

                      <p className="text-lg text-gray-600 mb-6 leading-relaxed whitespace-pre-line">
                        {service.description}
                      </p>

                      <Link to="/contact">
                        <Button
                          size="lg"
                          className="text-white font-semibold hover:scale-105 transition-transform duration-300"
                          style={{ backgroundColor: '#00CED1' }}
                        >
                          Request Consultation <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose <span style={{ color: '#00CED1' }}>Brainwave?</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our commitment to excellence sets us apart in the industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: '15+ Years', subtitle: 'Industry Experience', icon: '🏆' },
              { title: '200+', subtitle: 'Projects Completed', icon: '🏗️' },
              { title: '98%', subtitle: 'Client Satisfaction', icon: '⭐' },
              { title: '50+', subtitle: 'Design Awards', icon: '🎖️' },
            ].map((stat, index) => (
              <Card
                key={index}
                className="text-center p-8 border-0 hover:shadow-2xl transition-all duration-500"
                style={{ boxShadow: '0 4px 20px rgba(0, 206, 209, 0.1)' }}
              >
                <div className="text-5xl mb-4">{stat.icon}</div>
                <h3 className="text-4xl font-bold mb-2" style={{ color: '#00CED1' }}>
                  {stat.title}
                </h3>
                <p className="text-gray-600">{stat.subtitle}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#00CED1' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Build Something Amazing Together
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Contact us today to discuss your project and discover how we can help bring your vision to life.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white font-semibold px-8 py-6 text-base hover:scale-105 transition-transform duration-300"
              style={{ color: '#00CED1' }}
            >
              Get Started Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
